import { Button, Image, Input, Modal, Select, Space, Table, message, Row, Col } from "antd";
import { useContext, useEffect, useState } from "react";
import { HomeContext } from './HomeContext';
import { ApiConfig } from "../../services/ApiConfigs";
import { ApiServices } from "../../services/apiServices";
import { createShipment, getShipments, assignDriverToShipment } from "../../services/shipmentService";
import { PlusOutlined, UserAddOutlined } from "@ant-design/icons";

const { Search } = Input;
const { Option } = Select;

const ASSIGNMENT_MODE_KEY = 'Shipment Assignment Mode';

const AsignDriver = () => {

    const {
        asignDriverModalOpen,
        setAsignDriverModalOpen,
        submitAsignDriver,
        wareHouseData,
        driverData,
        selectedOrder,
        getOrders,
        getAssignOrders
    }: any = useContext(HomeContext);

    const [drivers, setDrivers] = useState([]);
    const [filteredDrivers, setFilteredDrivers] = useState([]);
    const [selectedWarehouse, setSelectedWarehouse] = useState(null as any);
    const [selectedDriver, setSelectedDriver] = useState(null as any);
    const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
    const [assignmentMode, setAssignmentMode] = useState<'admin_assign' | 'driver_accept'>('admin_assign');
    const [paymentType, setPaymentType] = useState<string>('COD');

    useEffect(() => {
        setDrivers(driverData);
        setFilteredDrivers(driverData);
    }, [driverData]);

    useEffect(() => {
        if (!asignDriverModalOpen) return;
        // Prefer warehouse already associated with the order/shipment
        const existingWhId =
            (selectedOrder?.shipments || [])?.find((s: any) => s?.pickupWarehouseId)?.pickupWarehouseId ||
            selectedOrder?.pickupWarehouseId ||
            selectedOrder?.warehouseId ||
            null;
        const existingWh = existingWhId
            ? (wareHouseData || []).find((wh: any) => Number(wh?.id) === Number(existingWhId))
            : null;

        if (existingWh) {
            setSelectedWarehouse(existingWh);
        } else if (Array.isArray(wareHouseData) && wareHouseData.length === 1) {
            setSelectedWarehouse(wareHouseData[0]);
        } else {
            setSelectedWarehouse(null);
        }
        setSelectedDriver(null);
        setSelectedRowKeys([]);
        setPaymentType(String(selectedOrder?.paymentType || 'COD').toUpperCase());
    }, [asignDriverModalOpen, selectedOrder?.id, wareHouseData]);

    useEffect(() => {
        if (asignDriverModalOpen) {
            ApiServices('get', ApiConfig.APP_CONFIG).then((res: any) => {
                const list = res?.data?.data || [];
                const row = list.find((r: any) => r.Key === ASSIGNMENT_MODE_KEY);
                if (row && (row.value === 'admin_assign' || row.value === 'driver_accept')) {
                    setAssignmentMode(row.value);
                }
            }).catch(() => {});
        }
    }, [asignDriverModalOpen]);

    // Table Columns
    const columns = [
        {
            title: 'Image',
            dataIndex: 'photoData',
            key: 'photoData',
            render: (image) => {
                return <Image
                    src={image?.url ? image?.url : "/image/user.png"}
                    width={40}
                    height={40}
                    style={{ borderRadius: 100 }}
                />
            }
        },
        {
            title: 'Name',
            dataIndex: 'firstName',
            key: 'firstName',
            render: (text, _) => <a>{text + " " + _.lastName}</a>,
            sorter: (a, b) => a.firstName.localeCompare(b.firstName),
        },
        // {
        //     title: 'Email',
        //     dataIndex: 'email',
        //     key: 'email',
        //     render: (text) => <a>{text}</a>,
        // },
        {
            title: 'Address',
            dataIndex: 'address',
            key: 'address',
            render: (text) => <a>{text}</a>,
        },
        {
            title: 'Available',
            dataIndex: 'isAvailable',
            key: 'isAvailable',
            render: (text) => <a style={{ color: text === "Offline" ? "gray" : 'green' }}>{text}</a>,
            filters: [
                { text: "Online", value: "Online" },
                { text: "Offline", value: "Offline" },
            ],
            onFilter: (value, record) => record.status === value,
        },
        {
            title: 'Phone',
            dataIndex: 'phone',
            key: 'phone',
            render: (text) => <a>{text}</a>,
        },
        {
            title: 'Vehicle Type',
            dataIndex: 'vehicleType',
            key: 'vehicleType',
            render: (text) => <a>{text}</a>,
        },
        // {
        //     title: 'Action',
        //     key: 'action',
        //     render: (_, record) => (
        //         <Space size="middle">
        //             {/* Add any action buttons here */}
        //         </Space>
        //     ),
        // },
    ];

    // Driver Search Handler
    const handleSearch = (value: string) => {
        const filtered = drivers.filter((driver: any) =>
            driver?.firstName.toLowerCase().includes(value.toLowerCase()) ||
            driver?.lastName.toLowerCase().includes(value.toLowerCase()) ||
            driver?.email.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredDrivers(filtered);
    };

    // Create shipment only (no driver) — for driver_accept mode; driver will accept later
    const handleCreateShipmentOnly = async () => {
        if (!selectedWarehouse) {
            message.error("Please select a warehouse!");
            return;
        }
        try {
            if (paymentType && String(selectedOrder?.paymentType || 'COD').toUpperCase() !== paymentType) {
                const payRes: any = await ApiServices('put', `${ApiConfig.ORDERS}/${selectedOrder?.id}/payment`, { paymentType });
                if (!payRes?.success && payRes?.status !== 200) {
                    message.error(payRes?.message || "Failed to update payment type.");
                    return;
                }
            }
            const shipmentsRes = await getShipments({ orderId: selectedOrder?.id });
            const existingShipments = shipmentsRes?.data || [];
            if (existingShipments.length > 0) {
                message.info("This order already has a shipment. Assign a driver below if needed.");
                return;
            }
            const response = await createShipment({
                orderId: selectedOrder?.id,
                storeId: selectedOrder?.store_id,
                pickupWarehouseId: selectedWarehouse.id,
            });
            if (response?.data || response?.message) {
                setAsignDriverModalOpen(false);
                message.success("Shipment created. A driver can accept it from their app.");
                getOrders?.();
                getAssignOrders?.();
            } else {
                message.error(response?.message || "Failed to create shipment.");
            }
        } catch (error: any) {
            message.error(error?.message || "Failed to create shipment.");
        }
    };

    // Assign driver (create shipment with driver or assign to existing shipment)
    const handleAssign = async () => {
        if (!selectedWarehouse) {
            message.error("Please select a warehouse!");
            return;
        }
        if (!selectedDriver) {
            message.error("Please select a driver!");
            return;
        }
        try {
            if (paymentType && String(selectedOrder?.paymentType || 'COD').toUpperCase() !== paymentType) {
                const payRes: any = await ApiServices('put', `${ApiConfig.ORDERS}/${selectedOrder?.id}/payment`, { paymentType });
                if (!payRes?.success && payRes?.status !== 200) {
                    message.error(payRes?.message || "Failed to update payment type.");
                    return;
                }
            }
            const shipmentsRes = await getShipments({ orderId: selectedOrder?.id });
            const existingShipments = shipmentsRes?.data || [];

            if (existingShipments.length > 0) {
                const shipment = existingShipments[0];
                const response = await assignDriverToShipment(shipment.id, selectedDriver.id);
                if (response?.message || response?.data) {
                    setAsignDriverModalOpen(false);
                    message.success("Driver assigned successfully");
                    getOrders?.();
                    getAssignOrders?.();
                } else {
                    message.error(response?.message || "Failed to assign driver.");
                }
            } else {
                const response = await createShipment({
                    orderId: selectedOrder?.id,
                    storeId: selectedOrder?.store_id,
                    driverId: selectedDriver.id,
                    pickupWarehouseId: selectedWarehouse.id,
                });
                if (response?.data || response?.message) {
                    setAsignDriverModalOpen(false);
                    message.success("Shipment created and driver assigned successfully");
                    getOrders?.();
                    getAssignOrders?.();
                } else {
                    message.error(response?.message || "Failed to create shipment.");
                }
            }
        } catch (error: any) {
            message.error(error?.message || "Failed to assign driver.");
        }
    };

    const handleChange = (value) => {
        // Find the selected warehouse object from the data
        const selected = wareHouseData.find((wh) => wh.id === value);
        console.log("Selected Warehouse Object:", selected);
        setSelectedWarehouse(selected);
    };

    return (
        <div>
            <Modal
                footer={null}
                title="Assign Driver to Order"
                width={980}
                open={asignDriverModalOpen}
                onOk={submitAsignDriver}
                onCancel={() => setAsignDriverModalOpen(false)}
                style={{ top: 24 }}
                styles={{ body: { padding: 0 } }}
            >
                <div style={{ padding: 16 }}>
                    {/* Top controls */}
                    <div
                        style={{
                            background: "#fff",
                            border: "1px solid #f0f0f0",
                            borderRadius: 12,
                            padding: 14,
                            marginBottom: 12,
                        }}
                    >
                        <Row gutter={[12, 12]} align="middle" wrap>
                            <Col xs={24} md={12} lg={10}>
                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                    <span style={{ fontWeight: 700, whiteSpace: "nowrap" }}>* Warehouse</span>
                                    <Select
                                        placeholder="Select warehouse"
                                        style={{ flex: 1, minWidth: 220 }}
                                        onChange={handleChange}
                                        value={selectedWarehouse?.id}
                                        disabled={Array.isArray(wareHouseData) && wareHouseData.length === 1}
                                    >
                                        {wareHouseData.map((wh) => (
                                            <Option key={wh.id} value={wh.id}>
                                                {wh.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </div>
                            </Col>

                            <Col xs={24} md={12} lg={8}>
                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                    <span style={{ fontWeight: 700, whiteSpace: "nowrap" }}>Payment</span>
                                    <Select
                                        value={paymentType}
                                        onChange={(v) => setPaymentType(String(v).toUpperCase())}
                                        style={{ flex: 1, minWidth: 220 }}
                                        dropdownMatchSelectWidth={false}
                                        placeholder="Select payment type"
                                    >
                                        <Option value="COD">COD</Option>
                                        <Option value="PREPAID">PREPAID</Option>
                                        <Option value="PARTIAL">PARTIAL</Option>
                                        <Option value="CREDIT">CREDIT</Option>
                                    </Select>
                                </div>
                            </Col>
                            {/* Actions on second line (below Warehouse/Payment) */}
                            <Col xs={24}>
                                <div style={{ display: "flex", justifyContent: "flex-start" }}>
                                    <Space size={8} wrap>
                                        {assignmentMode === 'driver_accept' && (
                                            <Button
                                                size="small"
                                                type="default"
                                                icon={<PlusOutlined />}
                                                onClick={handleCreateShipmentOnly}
                                                disabled={!selectedWarehouse}
                                                style={{
                                                    height: 32,
                                                    minWidth: 140,
                                                    borderRadius: 999,
                                                    fontWeight: 600,
                                                    borderColor: '#d9d9d9',
                                                    color: '#000',
                                                    opacity: 1, // keep label visible even when disabled
                                                }}
                                            >
                                                Create shipment
                                            </Button>
                                        )}
                                        <Button
                                            size="small"
                                            type="primary"
                                            icon={<UserAddOutlined />}
                                            onClick={() => handleAssign()}
                                            disabled={!selectedWarehouse || !selectedDriver}
                                            style={{
                                                height: 32,
                                                minWidth: 160,
                                                borderRadius: 999,
                                                fontWeight: 700,
                                                color: "#fff",
                                                background: '#1677ff',
                                                borderColor: '#1677ff',
                                            }}
                                        >
                                            {assignmentMode === 'driver_accept' ? 'Assign driver now' : 'Assign Order'}
                                        </Button>
                                    </Space>
                                </div>
                                {assignmentMode === 'driver_accept' && (
                                    <div style={{ marginTop: 6, textAlign: "left", fontSize: 11, color: "#8c8c8c", lineHeight: 1.4 }}>
                                        Create shipment: driver will accept from app
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </div>

                    {/* Driver Search */}
                    <Row gutter={16} style={{ marginTop: 10 }}>
                        <Col xs={24}>
                            <Search
                                placeholder="Search Drivers"
                                onSearch={handleSearch}
                                style={{ width: "100%" }}
                            />
                        </Col>
                    </Row>


                    {/* Driver List Table */}
                    <Table
                        columns={columns}
                        dataSource={filteredDrivers}
                        pagination={{ pageSize: 5 }}
                        size='small'
                        rowSelection={{
                            type: 'radio',
                            selectedRowKeys, // Bind selected row keys to state
                            onChange: (selectedKeys, selectedRows) => {
                                const selectedKey: any = selectedKeys.length > 0 ? [selectedKeys[selectedKeys.length - 1]] : [];
                                setSelectedRowKeys(selectedKey); // Ensure only one row is selected
                                setSelectedDriver(selectedRows[0]); // Store selected driver
                            },
                            getCheckboxProps: (record) => ({
                                disabled: false, // Ensure it's always selectable
                            }),
                        }}
                        rowKey="id" // Ensure each row has a unique key
                        scroll={{ x: 'max-content' }}
                        style={{ marginTop: 20 }}
                    />

                    {/* Assign Button */}
                    {/* <Row justify="end" style={{ marginTop: 16 }}>
                        <Col>
                            <Button
                                type="primary"
                                onClick={handleAssign}
                                disabled={!selectedWarehouse || !selectedDriver}
                            >
                                Assign Order
                            </Button>
                        </Col>
                    </Row> */}
                </div>
            </Modal>
        </div>
    );
};

export default AsignDriver;
